import { useCallback, useMemo } from "react";

import { useImportToaster, useModalDialog, type ModalDialogProps } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import {
  importDesignerNodeCreate,
  type AmbiguousLinkage,
  type ImportTarget,
  type PortableDesignerNodeInContext,
} from "@skilitics/nautilus-core-designer-data";

import { TemplateImportError } from "../errors/index.ts";
import { useSupportedLanguageCodes } from "../l10n/useSupportedLanguageCodes.ts";
import { collectBoundSlotRefs, distinctSlotRefs } from "../nodes/dereferenced/collectBoundSlotRefs.ts";
import { useMigrateSubtree } from "../nodes/migration/index.ts";
import { useActiveVariablesGroupedByParentContextTitle } from "../nodes/useActiveVariablesGroupedByParentContextTitle.ts";
import { useGetNodeTitleAndIsPlaceholder } from "../nodes/useGetNodeTitleAndIsPlaceholder.ts";
import { useResolveActiveNodes } from "../nodes/useResolveActiveNodes.ts";
import { useGetNextId, useGetNodeTypeId } from "../state/index.ts";
import { ResolveLinkages } from "./resolution/index.ts";
import type { ImportNodeOptions } from "./types.ts";
import { useSlotPathByIdMap } from "./useSlotPathByIdMap.ts";

const modalDialogId = "import-resolve-ambiguous-linkages";

const modalDialogProps: ModalDialogProps = {
  slotProps: {
    paper: {
      sx: {
        borderRadius: "5px",
        overflow: "hidden",
        width: "760px",
      },
    },
  },
};

export const useImportNodeDesign = ({ addDereferencedNode, onImportSlotRef, getTakenTitles }: ImportNodeOptions) => {
  const formatMessage = useFormatMessage(formatStrings);
  const getNodeTitle = useGetNodeTitleAndIsPlaceholder();
  const supportedLanguageCodes = useSupportedLanguageCodes();
  const migrateSubtree = useMigrateSubtree();
  const slotPathByIdMap = useSlotPathByIdMap();
  const getNodeTypeId = useGetNodeTypeId();
  const getNextId = useGetNextId();

  const resolveActiveNodes = useResolveActiveNodes();
  const activeVariablesGroupedByParentContextTitle = useActiveVariablesGroupedByParentContextTitle();

  const importToaster = useImportToaster();

  const { openModalDialog, closeModalDialog } = useModalDialog();

  const onResolveAmbiguousLinkages = useCallback(
    async (ambiguousLinkages: AmbiguousLinkage[]): Promise<[number, number][] | undefined> => {
      const linkages = ambiguousLinkages.map((ambiguousLinkage) => {
        const { nodeId, destinationSlotPath, basicNodeSpec, context } = ambiguousLinkage;

        const { parent } = destinationSlotPath[destinationSlotPath.length - 1];

        const nodeToLink = { id: nodeId, ...basicNodeSpec };

        const candidates = resolveActiveNodes(parent[1])
          .filter((node) => node.typeId === basicNodeSpec.typeId)
          .map((node) => {
            const { id } = node;
            const { title } = getNodeTitle(id);
            const grouping = activeVariablesGroupedByParentContextTitle(parent[1])[id] ?? formatMessage("activity");

            return { id, title, grouping };
          });

        return { nodeToLink, context, candidates };
      });

      const resolveLinkages = new Promise<[number, number][] | undefined>((resolve) => {
        let resolvedLinkages: [number, number][] | undefined = undefined;

        const onCancel = () => closeModalDialog(modalDialogId);

        const onResolve = (resolved: [number, number][]) => {
          resolvedLinkages = resolved;
          closeModalDialog(modalDialogId);
        };

        openModalDialog(
          {
            ...modalDialogProps,
            children: <ResolveLinkages linkages={linkages} onResolve={onResolve} onCancel={onCancel} />,
            onClose: () => resolve(resolvedLinkages),
          },
          modalDialogId,
        );
      });

      const resolvedLinkages = await resolveLinkages;

      // canceled
      if (resolvedLinkages === undefined) return undefined;

      // all resolved
      if (resolvedLinkages.length === linkages.length) return resolvedLinkages;

      const unresolvedLinkages = linkages.filter(
        ({ nodeToLink }) => !resolvedLinkages.some(([fromId]) => fromId === nodeToLink.id),
      );

      // show toast notifications with unresolved linkage details
      importToaster.setToasts(unresolvedLinkages.map(({ nodeToLink }) => nodeToLink));

      return undefined;
    },
    [
      activeVariablesGroupedByParentContextTitle,
      closeModalDialog,
      formatMessage,
      getNodeTitle,
      openModalDialog,
      resolveActiveNodes,
      importToaster,
    ],
  );

  return useMemo(() => {
    const importDesignerNode = importDesignerNodeCreate({
      getNextId,
      supportedLanguageCodes,
      migrateSubtree,
      addDereferencedNode,
      onResolveAmbiguousLinkages,
      getNodeTypeId,
      getTakenTitles,
    });

    return async (portableNode: PortableDesignerNodeInContext, importTarget: ImportTarget) => {
      if (portableNode.source.appVersion !== APP_VERSION) {
        throw new TemplateImportError(
          "This content is from a different version of the application. Please refresh the page and try again.",
        );
      }
      const importedNode = await importDesignerNode(portableNode, importTarget, { slotPathByIdMap });
      if (onImportSlotRef) {
        // Subject-subtree slot refs bind to the target layoutable; already validated by canBindSlotRef.
        for (const slotRef of distinctSlotRefs(collectBoundSlotRefs(portableNode.node))) {
          onImportSlotRef(slotRef);
        }
      }
      return importedNode;
    };
  }, [
    addDereferencedNode,
    onImportSlotRef,
    getTakenTitles,
    getNextId,
    getNodeTypeId,
    migrateSubtree,
    onResolveAmbiguousLinkages,
    slotPathByIdMap,
    supportedLanguageCodes,
  ]);
};

const formatStrings = {
  activity: {
    en: "Activity",
  },
} satisfies FormatMessages;
